import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import Input from 'components/form/input/Input';
import {
  Body,
  Error as ErrorMessage,
  Title2,
} from 'components/typography/Typography';
import { UPDATE_ACCOUNT_SETTINGS } from 'graphql/users/mutations';
import { GET_USER } from 'graphql/users/queries';
import { useQuery } from 'hooks/sympl-query';
import useUnsavedFields from 'hooks/unsaved';
import { Controller, useForm } from 'react-hook-form';
import Container from 'components/container/Container';
import ContainerHeader from 'components/container/container-header/ContainerHeader';
import ContainerAction from 'components/container/container-action/ContainerAction';
import SaveButton from 'components/save-button/SaveButton';

import { AccountSettingsForm } from '../AccountSettings';

import 'twin.macro';
import { ToastTypes } from 'types/notificationTypes';
import { useToastNotifications } from 'hooks/notificationHooks';
import {
  AccountSettingsData,
  AccountSettingsFormData,
  buildAccountSettingsData,
  getAccountSettingsErrorMessage,
  getPasswordSettingsError,
} from './settingsPayload';

interface AccountSettingsPayload {
  input: AccountSettingsData;
}

type UserData = {
  firstname: string;
  lastname: string;
  job_position: string;
  signature: string;
};

const isPasswordField = (str: string): boolean => str.includes('password');

const toTitleCase = (str: string) => {
  return str
    .replace(/-/g, ' ')
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const GenericSettings: React.FC<{
  title: string;
  fields: AccountSettingsForm[];
}> = ({ title, fields }) => {
  const formMethods = useForm();
  const formRef = useRef<HTMLFormElement>(null);
  const { control, getValues, handleSubmit, setValue } = formMethods;
  const { addToast } = useToastNotifications();

  const [shouldSaveChanges, resetUnsavedFlag] = useUnsavedFields(formMethods);

  const { data: userData, refetch: refetchUserData } = useQuery<{
    user: UserData;
  }>(GET_USER);

  useEffect(() => {
    setValue('firstname', userData?.user.firstname);
    setValue('lastname', userData?.user.lastname);
    setValue('job-position', userData?.user.job_position);
    setValue('signature-job-position', userData?.user.signature);
  }, [userData, setValue]);

  const [
    updateAccountSettings,
    {
      loading: accountSettingsLoading,
      error: accountSettingsError,
      reset: resetAccountSettings,
    },
  ] = useMutation<undefined, AccountSettingsPayload>(UPDATE_ACCOUNT_SETTINGS);

  const isPasswordSettings = fields.some(isPasswordField);
  const [passwordSettingsError, setPasswordSettingsError] = useState<string>();
  const accountSettingsErrorMessage = getAccountSettingsErrorMessage(
    accountSettingsError,
    isPasswordSettings
  );
  const settingsErrorMessage =
    passwordSettingsError ?? accountSettingsErrorMessage;

  const getSubmittedData = (
    data: AccountSettingsFormData
  ): AccountSettingsFormData => {
    if (!formRef.current) return data;

    const submittedFormData = new FormData(formRef.current);
    return fields.reduce<AccountSettingsFormData>(
      (submittedData, field) => ({
        ...submittedData,
        [field]: submittedFormData.get(field)?.toString() ?? data[field],
      }),
      data
    );
  };

  const submitHandler = async (data: AccountSettingsFormData) => {
    const submittedData = getSubmittedData(data);
    const validationError = isPasswordSettings
      ? getPasswordSettingsError(submittedData)
      : undefined;

    setPasswordSettingsError(validationError);
    if (validationError) return;

    try {
      await updateAccountSettings({
        variables: {
          input: buildAccountSettingsData(fields, submittedData),
        },
      });
      addToast({
        type: ToastTypes.SUCCESS,
        description: 'Your settings have been successfully updated',
      });
      refetchUserData();
    } catch (_error) {
      // Apollo exposes the server error through accountSettingsError.
    }
  };

  const isRequiredField = (key: AccountSettingsForm) => {
    return key.includes('firstname') || key.includes('lastname');
  };

  function getDefaultValue(key: AccountSettingsForm): string | number {
    const defaultValues: Record<AccountSettingsForm, string | number> = {
      'new-password': '',
      'repeat-password': '',
      password: '',
      'job-position': userData?.user.job_position ?? '',
      firstname: userData?.user.firstname ?? '',
      lastname: userData?.user.lastname ?? '',
      'signature-job-position': userData?.user.signature ?? '',
    };

    return defaultValues[key] ?? '';
  }

  const getAutoComplete = (field: AccountSettingsForm) => {
    if (field === 'password') return 'current-password';
    if (field === 'new-password' || field === 'repeat-password')
      return 'new-password';

    return undefined;
  };

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  ) => {
    onChange(event);
    setPasswordSettingsError(undefined);
    if (accountSettingsError) resetAccountSettings();
  };

  const validatePasswordBeforeSave = async () => {
    if (!isPasswordSettings) return;

    const validationError = getPasswordSettingsError(
      getSubmittedData(getValues() as AccountSettingsFormData)
    );
    setPasswordSettingsError(validationError);

    if (validationError) throw new Error(validationError);
  };

  return (
    <Container>
      <form
        ref={formRef}
        onSubmit={handleSubmit(submitHandler)}
        autoComplete="on"
      >
        <>
          <ContainerHeader>
            <Title2 mb={3}>{title}</Title2>
          </ContainerHeader>

          <div tw="mt-1 flex flex-col gap-3 md:flex-row">
            {fields.map((field: AccountSettingsForm, i) => {
              const title = toTitleCase(field);
              return (
                <div tw="flex-1" key={i}>
                  <Body mb={1}>{title}</Body>
                  <Controller
                    id={field}
                    name={field}
                    control={control}
                    defaultValue={getDefaultValue(field)}
                    render={({ onChange, value }) => {
                      return (
                        <Input
                          type={isPasswordField(field) ? 'password' : 'text'}
                          id={field}
                          name={field}
                          required={isRequiredField(field)}
                          defaultValue={value}
                          autoComplete={getAutoComplete(field)}
                          placeholder={title}
                          onChange={(event) =>
                            handleFieldChange(event, onChange)
                          }
                        />
                      );
                    }}
                  />
                </div>
              );
            })}
          </div>
          {isPasswordSettings ? (
            <div tw="mt-2 min-h-[20px]" aria-live="polite">
              {settingsErrorMessage && (
                <ErrorMessage mt={0}>{settingsErrorMessage}</ErrorMessage>
              )}
            </div>
          ) : (
            settingsErrorMessage && (
              <ErrorMessage>{settingsErrorMessage}</ErrorMessage>
            )
          )}
        </>
        <ContainerAction>
          <div tw="ml-auto flex flex-row gap-2">
            <SaveButton
              type="submit"
              loading={accountSettingsLoading}
              shouldSave={shouldSaveChanges}
              error={Boolean(passwordSettingsError) || accountSettingsError}
              labelError={
                passwordSettingsError
                  ? 'Some fields are incorrect'
                  : isPasswordSettings
                  ? 'Password not changed'
                  : 'Settings not saved'
              }
              onClick={validatePasswordBeforeSave}
              onDone={resetUnsavedFlag}
            />
          </div>
        </ContainerAction>
      </form>
    </Container>
  );
};
